/**
 * The users register.
 *
 * Two things this screen exists to make visible, because they are the usual
 * causes of "why can they not sign in":
 *
 * - a **user with no roles** can sign in and do nothing, so the role count is a
 *   column rather than something to click into;
 * - a **user with no name** is a half-provisioned account — a `User` is a pure
 *   authentication row and the name lives on the employee record behind it — so
 *   it is shown as incomplete rather than blank.
 */

import { useMemo, useState } from 'react';
import { KeyRound, Plus, ShieldAlert, UserRound } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Can,
  PageHeader,
  ServerTable,
  StatusChip,
  createFacetsFetcher,
  createServerFetcher,
  formatDateTime,
  formatRelative,
  type ServerColumn,
} from '@connect/web-shared';

import { NewUserDialog } from './new-user-dialog';
import { UserDetailDialog } from './user-detail-dialog';

interface UserRow {
  public_id: string;
  username: string;
  email: string;
  full_name: string;
  job_title: string | null;
  code: string | null;
  department_name: string | null;
  is_active: boolean;
  is_superuser: boolean;
  must_change_password: boolean;
  role_count: number;
  last_login_at: string | null;
  created_at: string;
}

const ENDPOINT = '/iam/users';

export function UsersPage() {
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState<string | null>(null);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const fetchRows = useMemo(() => createServerFetcher<UserRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<UserRow>[]>(
    () => [
      {
        field: 'full_name',
        header: 'Person',
        icon: <UserRound className="size-3" />,
        filter: 'text',
        minWidth: 200,
        render: (row) => (
          <span className="flex flex-col">
            {row.full_name ? (
              <span className="truncate font-medium">{row.full_name}</span>
            ) : (
              // Not blank. This is a real state with a real cause, and the
              // register is where somebody comes to diagnose it.
              <span className="truncate text-warning">Account not completed</span>
            )}
            <span className="truncate text-2xs text-faint">
              {[row.username, row.email].filter(Boolean).join(' · ')}
            </span>
          </span>
        ),
      },
      { field: 'job_title', header: 'Job title', filter: 'text', minWidth: 150 },
      {
        field: 'department_name',
        header: 'Department',
        filter: 'select',
        width: 170,
      },
      {
        field: 'role_count',
        header: 'Roles',
        numeric: true,
        width: 90,
        sortable: false,
        render: (row) =>
          row.role_count === 0 ? (
            // Zero roles is the answer to "why can they not do anything",
            // so it is called out rather than shown as a quiet 0.
            <Badge tone="warning">None</Badge>
          ) : (
            <span className="tabular">{row.role_count}</span>
          ),
      },
      {
        field: 'is_active',
        header: 'Active',
        filter: 'boolean',
        align: 'center',
        width: 90,
        render: (row) =>
          row.is_active ? (
            <StatusChip value="active" tones={{ active: 'success' }} />
          ) : (
            <StatusChip value="disabled" tones={{ disabled: 'danger' }} />
          ),
      },
      {
        field: 'is_superuser',
        header: 'Superuser',
        filter: 'boolean',
        align: 'center',
        width: 110,
        hiddenByDefault: true,
        render: (row) =>
          row.is_superuser ? (
            <Badge tone="danger">
              <ShieldAlert className="size-2.5" />
              Yes
            </Badge>
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        field: 'must_change_password',
        header: 'Password',
        filter: 'boolean',
        width: 130,
        hiddenByDefault: true,
        render: (row) =>
          row.must_change_password ? (
            <Badge tone="warning">
              <KeyRound className="size-2.5" />
              Must change
            </Badge>
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        field: 'last_login_at',
        header: 'Last signed in',
        filter: 'date',
        width: 160,
        render: (row) =>
          row.last_login_at ? (
            // The relative time is quicker to read and impossible to cite, so
            // the absolute one is on hover. Both, one visible.
            <span title={formatDateTime(row.last_login_at)}>
              {formatRelative(row.last_login_at)}
            </span>
          ) : (
            <span className="text-faint">Never</span>
          ),
      },
      {
        field: 'created_at',
        header: 'Created',
        filter: 'date',
        width: 130,
        hiddenByDefault: true,
        format: (value) => formatDateTime(value as string | null),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        description="Everybody who can sign in to this installation."
        breadcrumbs={[{ label: 'Access' }, { label: 'Users' }]}
        actions={
          <Can permission="iam.user.create">
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-3.5" />
              New account
            </Button>
          </Can>
        }
      />

      <Alert tone="info" title="Deactivating an account takes effect immediately">
        Authority is resolved from the database on every request, so a deactivated account stops
        working on its next call rather than when its token expires.
      </Alert>

      <ServerTable<UserRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['iam', 'users']}
        stateKey="iam.users"
        searchPlaceholder="Search by name, code or job title"
        viewField="full_name"
        onView={(row) => setOpened(row.public_id)}
        recentKey={recentKey}
        emptyTitle="No users"
        emptyDescription="Every installation has at least the owner account created during setup. If this is empty, the seeder has not run."
      />

      <NewUserDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(publicId) => setRecentKey(publicId)}
      />

      <UserDetailDialog
        open={opened !== null}
        publicId={opened}
        onClose={() => setOpened(null)}
      />
    </div>
  );
}
